import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Mail, Check, X, RefreshCw, PackageSearch } from 'lucide-react';
import WhatsAppIcon from './icons/WhatsAppIcon';

const EVENT_LABELS = {
  package_created: 'Créé',
  collected: 'Collecté',
  in_transit: 'En transit',
  customs_cleared: 'Dédouané',
  arrived: 'Arrivé',
  out_for_delivery: 'En livraison',
  ready_for_pickup: 'Point relais',
  delivered: 'Livré',
};

// Étape (événement de notification) → statut colis correspondant, pour savoir
// si l'étape a été réellement atteinte (progression indépendante des notifs).
const EVENT_STATUS = { package_created: 'registered' };
const statusForEvent = (ev) => EVENT_STATUS[ev] || ev;

// Chip d'un canal : vert = envoyé, rouge = échec, gris = rien.
const ChannelChip = ({ icon: Icon, state }) => {
  const cls = state === 'sent'
    ? 'bg-green-100 text-green-700'
    : state === 'failed'
      ? 'bg-red-100 text-red-700'
      : 'bg-muted text-muted-foreground/40';
  return (
    <span className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 ${cls}`} title={state || 'aucun'}>
      <Icon size={10} />
      {state === 'sent' ? <Check size={9} /> : state === 'failed' ? <X size={9} /> : <span className="text-[9px]">–</span>}
    </span>
  );
};

// Ligne d'une audience (Exp / Dest) : ses deux canaux WhatsApp + email.
const AudienceRow = ({ label, chips }) => (
  <div className="flex items-center justify-center gap-1">
    <span className="text-[9px] text-muted-foreground w-7 text-right shrink-0">{label}</span>
    <ChannelChip icon={WhatsAppIcon} state={chips?.whatsapp} />
    <ChannelChip icon={Mail} state={chips?.email} />
  </div>
);

const NotificationAudit = () => {
  const [shipments, setShipments] = useState([]);
  const [mode, setMode] = useState('shipment'); // 'shipment' | 'package'
  const [shipmentId, setShipmentId] = useState('');
  const [oce, setOce] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/shipments');
        setShipments(res.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const fetchAudit = useCallback(async () => {
    const params = new URLSearchParams();
    if (mode === 'shipment' && shipmentId) params.set('shipment_id', shipmentId);
    if (mode === 'package' && oce.trim()) params.set('oce_code', oce.trim());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if ([...params.keys()].length === 0) { setData(null); return; }
    setLoading(true); setTouched(true);
    try {
      const res = await api.get(`/api/audit/notifications?${params.toString()}`);
      setData(res.data);
    } catch { setData(null); } finally { setLoading(false); }
  }, [mode, shipmentId, oce, dateFrom, dateTo]);

  // Auto-fetch quand on choisit un départ (sans date).
  useEffect(() => {
    if (mode === 'shipment' && shipmentId && !dateFrom && !dateTo) fetchAudit();
  }, [shipmentId, mode, dateFrom, dateTo, fetchAudit]);

  // Index : cell[packageId][event][audience][channel] = 'sent' | 'failed' | null
  const buildIndex = () => {
    const idx = {};
    for (const log of (data?.logs || [])) {
      const p = log.package_id;
      const ev = log.event;
      const aud = log.audience === 'recipient' ? 'recipient' : 'sender';
      const ch = log.channel === 'email' ? 'email' : 'whatsapp';
      if (!idx[p]) idx[p] = {};
      if (!idx[p][ev]) idx[p][ev] = { sender: { whatsapp: null, email: null }, recipient: { whatsapp: null, email: null } };
      // 'sent' l'emporte sur 'failed'
      if (idx[p][ev][aud][ch] !== 'sent') idx[p][ev][aud][ch] = log.status;
    }
    return idx;
  };
  const idx = buildIndex();
  const events = data?.events_order || [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <PackageSearch size={18} className="text-orange-500" />
          Audit des notifications
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Pour chaque colis et chaque étape : le statut est-il atteint, et l'expéditeur / le destinataire ont-ils été notifiés (WhatsApp / email) ?
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {/* Mode */}
          <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
            <button
              onClick={() => { setMode('shipment'); setData(null); setTouched(false); }}
              className={`px-3 py-1.5 ${mode === 'shipment' ? 'bg-orange-500 text-white' : 'bg-background hover:bg-muted'}`}
            >Par départ</button>
            <button
              onClick={() => { setMode('package'); setData(null); setTouched(false); }}
              className={`px-3 py-1.5 border-l border-border ${mode === 'package' ? 'bg-orange-500 text-white' : 'bg-background hover:bg-muted'}`}
            >Par colis</button>
          </div>

          {/* Filtres */}
          <div className="flex items-end gap-3 flex-wrap">
            {mode === 'shipment' ? (
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Départ</label>
                <select
                  value={shipmentId}
                  onChange={(e) => setShipmentId(e.target.value)}
                  className="min-w-[240px] border border-input rounded-md px-3 py-2 text-sm bg-background"
                >
                  <option value="">Choisir un départ…</option>
                  {shipments.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.origin_country && s.destination_country ? ` (${s.origin_country} → ${s.destination_country})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Code OCE du colis</label>
                <input
                  value={oce}
                  onChange={(e) => setOce(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchAudit(); }}
                  placeholder="OCE12345678"
                  className="w-[200px] border border-input rounded-md px-3 py-2 text-sm bg-background font-mono"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Du</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border border-input rounded-md px-2 py-2 text-sm bg-background" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Au</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border border-input rounded-md px-2 py-2 text-sm bg-background" />
            </div>
            <button onClick={fetchAudit} className="inline-flex items-center gap-1 rounded-md bg-orange-500 text-white px-3 py-2 text-sm hover:bg-orange-600">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Appliquer
            </button>
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs text-muted-foreground hover:text-foreground">Effacer dates</button>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
            <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-green-500" /> statut atteint</span>
            <span className="inline-flex items-center gap-1">Exp = expéditeur · Dest = destinataire</span>
            <span className="inline-flex items-center gap-1"><WhatsAppIcon size={12} className="text-green-600" /> WhatsApp</span>
            <span className="inline-flex items-center gap-1"><Mail size={12} /> Email</span>
            <span className="inline-flex items-center gap-1"><Check size={12} className="text-green-600" /> envoyé</span>
            <span className="inline-flex items-center gap-1"><X size={12} className="text-red-600" /> échec</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : !touched ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Choisissez un départ, un colis (OCE) ou une période, puis « Appliquer ».</p>
        ) : !data?.packages?.length ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Aucun colis pour ce filtre.</p>
        ) : (
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th className="p-2 sticky left-0 bg-muted/50 z-10">Colis</th>
                  {events.map((ev) => (
                    <th key={ev} className="p-2 text-center font-medium whitespace-nowrap">{EVENT_LABELS[ev] || ev}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.packages.map((pkg) => {
                  const reached = new Set(pkg.reached_statuses || []);
                  return (
                    <tr key={pkg.id} className="border-b last:border-0 align-top">
                      <td className="p-2 sticky left-0 bg-card z-10">
                        <div className="font-medium">{pkg.oce_code}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[180px]" title={pkg.sender_name || ''}>
                          <span className="text-muted-foreground/60">Exp :</span> {pkg.sender_name || '—'}
                        </div>
                        <div className="text-xs text-muted-foreground truncate max-w-[180px]" title={pkg.recipient_name || ''}>
                          <span className="text-muted-foreground/60">Dest :</span> {pkg.recipient_name || '—'}
                        </div>
                      </td>
                      {events.map((ev) => {
                        const cell = idx[pkg.id]?.[ev];
                        const isReached = reached.has(statusForEvent(ev));
                        return (
                          <td key={ev} className="p-2">
                            <div className="flex flex-col items-center gap-1">
                              <span
                                title={isReached ? 'Statut atteint' : 'Non atteint'}
                                className={`inline-block w-2 h-2 rounded-full ${isReached ? 'bg-green-500' : 'bg-muted-foreground/20'}`}
                              />
                              <AudienceRow label="Exp" chips={cell?.sender} />
                              <AudienceRow label="Dest" chips={cell?.recipient} />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationAudit;
